/**
 * Product comparison table — differences toggle, column swap, mobile dots.
 *
 * Only the visible columns are in the DOM. Picking an off-screen product
 * fetches that product's PDP with `section_id` and lifts its column (the
 * locked first column there) out of the response. Every product shares
 * product.json, so the section id and row block ids line up. The column
 * that leaves the screen is kept, so it can come back without another fetch.
 */
(() => {
  const TAG = 'product-comparison';

  if (customElements.get(TAG)) return;

  const DESKTOP_QUERY = '(min-width: 1024px)';

  const readPool = (root) => {
    const source = root.querySelector('[data-comparison-pool]');

    if (!source) return [];

    try {
      const pool = JSON.parse(source.textContent || '[]');

      return Array.isArray(pool) ? pool : [];
    } catch {
      return [];
    }
  };

  // Stand-in for a cell the fetched section did not render, so the row keeps
  // one cell per column and the grid does not shift.
  const placeholderCell = (cell, productId, emptyLabel) => {
    const placeholder = cell.cloneNode(false);

    placeholder.setAttribute('data-product-id', productId);

    if (placeholder.hasAttribute('data-compare')) {
      const text = document.createElement('span');

      placeholder.setAttribute('data-compare', '');
      placeholder.classList.add('product-comparison__cell-empty');
      text.className = 'product-comparison__cell-text';
      text.textContent = emptyLabel;
      placeholder.append(text);
    }

    return placeholder;
  };

  // Index of the column whose leading edge sits closest to the gutter when the
  // scroller is at `target`.
  const columnIndexAt = (layout, target) => {
    let active = 0;
    let best = Number.POSITIVE_INFINITY;

    for (const [index, offset] of layout.offsets.entries()) {
      const delta = Math.abs(offset - target - layout.gutter);

      if (delta < best) {
        best = delta;
        active = index;
      }
    }

    return active;
  };

  const pageIndexes = (layout) => {
    const indexes = new Set([columnIndexAt(layout, 0), columnIndexAt(layout, layout.maxScroll)]);

    for (const offset of layout.offsets) {
      const target = Math.max(0, Math.min(layout.maxScroll, offset - layout.gutter));

      indexes.add(columnIndexAt(layout, target));
    }

    return [...indexes].sort((a, b) => a - b);
  };

  // Parsed in a template, which stays inert. Upgrading the fetched host would
  // bind window listeners that nothing ever removes, and keep every row of
  // that product's section alive. Only this column's header and cells remain.
  const columnFromSection = (html, productId) => {
    const template = document.createElement('template');
    const selector = `[data-product-id="${CSS.escape(productId)}"]`;

    template.innerHTML = html;

    const host = template.content.querySelector(TAG);
    const header = host?.querySelector(`[data-comparison-product]${selector}`);

    if (!header) throw new Error('Comparison column missing from section response');

    const cells = new Map();

    for (const row of host.querySelectorAll('[data-comparison-row]')) {
      const rowId = row.dataset.comparisonRow;

      if (!rowId) continue;

      const group = row.querySelector(`[data-comparison-group] ${selector}`);
      const data = row.querySelector(`[data-comparison-data] ${selector}`);

      group?.remove();
      data?.remove();
      cells.set(rowId, { group, data });
    }

    header.remove();

    return { header, cells };
  };

  class ProductComparison extends HTMLElement {
    #toggle = null;
    #scroller = null;
    #dotsHost = null;
    #desktopQuery = null;
    #pool = [];
    #columnRequests = new Map();
    #layout = null;
    #scrollFrame = 0;
    #resizeFrame = 0;

    connectedCallback() {
      // A host parsed into another document must not bind this window.
      if (this.ownerDocument !== document) return;

      this.#toggle = this.querySelector('[data-comparison-toggle]');
      this.#scroller = this.querySelector('[data-comparison-scroller]');
      this.#dotsHost = this.querySelector('[data-comparison-dots]');
      this.#pool = readPool(this);

      this.#bind();
      this.#syncRows();
      this.#syncGroups();
      this.#syncSelectOptions();
      this.#syncDots();
    }

    disconnectedCallback() {
      this.#toggle?.removeEventListener('change', this.#onToggle);
      this.removeEventListener('change', this.#onSelect);
      this.#scroller?.removeEventListener('scroll', this.#onScroll);
      this.#desktopQuery?.removeEventListener('change', this.#onViewportChange);
      window.removeEventListener('resize', this.#onViewportChange);
      window.cancelAnimationFrame(this.#scrollFrame);
      window.cancelAnimationFrame(this.#resizeFrame);

      for (const dot of this.#dotsHost?.children ?? []) {
        dot.removeEventListener('click', this.#onDotClick);
      }
    }

    get #isDesignMode() {
      return this.dataset.designMode === 'true';
    }

    #bind() {
      this.#toggle?.addEventListener('change', this.#onToggle);

      // Delegated, because a swap replaces the select along with its column.
      this.addEventListener('change', this.#onSelect);

      this.#scroller?.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.#desktopQuery.addEventListener('change', this.#onViewportChange);
      window.addEventListener('resize', this.#onViewportChange);
    }

    #onToggle = () => {
      const on = Boolean(this.#toggle?.checked);

      this.classList.toggle('is-differences-only', on);
      this.#toggle?.setAttribute('aria-checked', on ? 'true' : 'false');
      this.#syncRows();
      this.#syncGroups();
    };

    #onSelect = async (event) => {
      const select = event.target;

      if (!(select instanceof HTMLSelectElement) || !select.matches('[data-comparison-select]')) return;

      const column = select.closest('[data-comparison-product]');
      const currentId = column?.getAttribute('data-product-id');
      const nextId = select.value;

      if (!column || !currentId || !nextId) return;

      if (nextId === currentId) {
        delete column.dataset.pendingId;
        column.removeAttribute('aria-busy');
        this.#syncSelectOptions();
        return;
      }

      if (this.#columnFor(nextId) || this.#pendingElsewhere(column, nextId)) {
        select.value = currentId;
        return;
      }

      // A later pick on the same column supersedes this one.
      column.dataset.pendingId = nextId;
      column.setAttribute('aria-busy', 'true');
      this.#syncSelectOptions();

      const source = await this.#loadColumn(nextId);

      if (!column.isConnected || column.dataset.pendingId !== nextId) return;

      delete column.dataset.pendingId;
      column.removeAttribute('aria-busy');

      // The other column may have taken this product while the request ran.
      if (!source || this.#columnFor(nextId)) {
        select.value = currentId;
        this.#syncSelectOptions();
        return;
      }

      // Checked after the wait. The shopper may have moved to another control while this column loaded.
      const stillFocused = document.activeElement === select;

      this.#rememberColumn(currentId, column);

      const nextColumn = this.#replaceColumn(column, currentId, nextId, source);

      this.#syncSelectOptions();
      this.#syncRows();
      this.#syncGroups();
      this.#syncDots();

      if (stillFocused) {
        nextColumn.querySelector('[data-comparison-select]')?.focus({ preventScroll: true });
      }
    };

    #onViewportChange = () => {
      window.cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = window.requestAnimationFrame(() => {
        this.#resizeFrame = 0;
        this.#syncDots();
      });
    };

    #onScroll = () => {
      if (this.#scrollFrame) return;

      this.#scrollFrame = window.requestAnimationFrame(() => {
        this.#scrollFrame = 0;
        this.#updateActiveDot();
      });
    };

    #onDotClick = (event) => {
      const index = Number(event.currentTarget.dataset.comparisonDot);

      if (Number.isNaN(index)) return;

      this.#scrollColumnIntoView(index);
    };

    #columns() {
      return Array.from(this.querySelectorAll('[data-comparison-product]'));
    }

    #columnFor(productId) {
      return this.querySelector(`[data-comparison-product][data-product-id="${CSS.escape(productId)}"]`);
    }

    #pendingElsewhere(column, productId) {
      for (const other of this.#columns()) {
        if (other !== column && other.dataset.pendingId === productId) return true;
      }

      return false;
    }

    // The column on screen is discarded by the swap. Keep a copy so picking it
    // again does not depend on that product's page rendering this section.
    #rememberColumn(productId, column) {
      const cells = new Map();
      const selector = `[data-product-id="${CSS.escape(productId)}"]`;

      for (const row of this.querySelectorAll('[data-comparison-row]')) {
        const rowId = row.dataset.comparisonRow;

        if (!rowId) continue;

        const group = row.querySelector(`[data-comparison-group] ${selector}`);
        const data = row.querySelector(`[data-comparison-data] ${selector}`);

        cells.set(rowId, {
          group: group ? document.importNode(group, true) : null,
          data: data ? document.importNode(data, true) : null,
        });
      }

      const header = document.importNode(column, true);

      header.removeAttribute('aria-busy');
      delete header.dataset.pendingId;
      this.#columnRequests.set(productId, Promise.resolve({ header, cells }));
    }

    #loadColumn(productId) {
      if (!this.#columnRequests.has(productId)) {
        this.#columnRequests.set(productId, this.#fetchColumn(productId));
      }

      return this.#columnRequests.get(productId);
    }

    async #fetchColumn(productId) {
      const entry = this.#pool.find((item) => String(item.id) === productId);
      const sectionId = this.dataset.sectionId;

      if (!entry?.url || !sectionId) return null;

      try {
        const url = new URL(entry.url, window.location.origin);

        url.searchParams.set('section_id', sectionId);

        const response = await fetch(url, { headers: { Accept: 'text/html' } });

        if (!response.ok) throw new Error(`Comparison column request failed: ${response.status}`);

        return columnFromSection(await response.text(), productId);
      } catch {
        // Let the next pick retry instead of caching the failure.
        this.#columnRequests.delete(productId);
        return null;
      }
    }

    #replaceColumn(column, currentId, nextId, { header, cells }) {
      const nextColumn = document.importNode(header, true);
      const nextSelect = nextColumn.querySelector('[data-comparison-select]');
      const emptyLabel = this.dataset.emptyLabel || '';

      nextColumn.removeAttribute('data-comparison-locked');

      if (nextSelect) nextSelect.disabled = false;

      for (const row of this.querySelectorAll('[data-comparison-row]')) {
        const source = cells.get(row.dataset.comparisonRow) ?? {};

        for (const [part, sourceCell] of [
          ['[data-comparison-group]', source.group],
          ['[data-comparison-data]', source.data],
        ]) {
          const cell = row.querySelector(`${part} [data-product-id="${CSS.escape(currentId)}"]`);

          if (!cell) continue;

          cell.replaceWith(
            sourceCell ? document.importNode(sourceCell, true) : placeholderCell(cell, nextId, emptyLabel)
          );
        }
      }

      column.replaceWith(nextColumn);

      return nextColumn;
    }

    #syncSelectOptions() {
      const columns = this.#columns();
      const shown = new Set();

      for (const column of columns) {
        const id = column.getAttribute('data-product-id');

        if (id) shown.add(id);
        if (column.dataset.pendingId) shown.add(column.dataset.pendingId);
      }

      for (const column of columns) {
        if (column.hasAttribute('data-comparison-locked')) continue;

        const select = column.querySelector('[data-comparison-select]');

        if (!select) continue;

        const currentId = column.getAttribute('data-product-id') || '';
        const pendingId = column.dataset.pendingId || '';
        const selectedId = pendingId || currentId;
        const fragment = document.createDocumentFragment();
        const titleFor = (productId) => {
          if (productId === currentId) return column.dataset.productTitle || '';

          return this.#pool.find((item) => String(item.id) === productId)?.title || '';
        };
        const addOption = (productId, selected) => {
          const option = document.createElement('option');

          option.value = productId;
          option.selected = selected;
          option.textContent = titleFor(productId);
          fragment.append(option);
        };

        addOption(selectedId, true);

        // The current product stays selectable so an in-flight pick can be cancelled.
        if (pendingId && currentId && currentId !== pendingId) addOption(currentId, false);

        for (const item of this.#pool) {
          const id = String(item.id);

          if (shown.has(id)) continue;

          addOption(id, false);
        }

        select.replaceChildren(fragment);
      }
    }

    // One read pass for everything the dots need. Column offsets are in
    // scroll-content coordinates, which do not move as the scroller scrolls,
    // so scroll updates only have to read scrollLeft.
    #measure() {
      if (this.#layout) return this.#layout;

      const scroller = this.#scroller;
      const scrollerLeft = scroller.getBoundingClientRect().left;
      const scrollLeft = scroller.scrollLeft;
      const gutter = this.querySelector('.product-comparison__toggle-cell')?.getBoundingClientRect().width ?? 0;

      this.#layout = {
        gutter,
        offsets: this.#columns().map((column) => column.getBoundingClientRect().left - scrollerLeft + scrollLeft),
        maxScroll: Math.max(0, scroller.scrollWidth - scroller.clientWidth),
        scrollLeft,
      };

      return this.#layout;
    }

    #scrollColumnIntoView(index) {
      const scroller = this.#scroller;
      const layout = scroller ? this.#measure() : null;
      const offset = layout?.offsets[index];

      if (!scroller || offset === undefined) return;

      scroller.style.scrollSnapType = 'none';

      let restored = false;
      const restore = () => {
        if (restored) return;

        restored = true;
        scroller.style.scrollSnapType = '';
        scroller.removeEventListener('scrollend', restore);
        this.#syncDots();
      };

      scroller.addEventListener('scrollend', restore);
      scroller.scrollTo({ left: Math.max(0, offset - layout.gutter), behavior: 'smooth' });
      window.setTimeout(restore, 450);
    }

    #syncRows() {
      const differencesOnly = this.classList.contains('is-differences-only');
      const rows = this.querySelectorAll('[data-comparison-data]');

      for (const row of rows) {
        const cells = Array.from(row.querySelectorAll('[data-compare]'));
        const values = cells.map((cell) => cell.getAttribute('data-compare') || '');
        const allEmpty = values.every((value) => value === '');
        const allSame = values.length > 1 && values.every((value) => value === values[0]);
        const parent = row.closest('.product-comparison-row');

        let hidden = false;

        if (!this.#isDesignMode && allEmpty) hidden = true;
        if (differencesOnly && allSame) hidden = true;

        if (parent) parent.classList.toggle('is-same', hidden);
      }
    }

    #syncGroups() {
      const rows = Array.from(this.querySelectorAll('.product-comparison-row'));
      let lastVisibleGroup = null;

      for (const row of rows) {
        const group = row.querySelector('[data-comparison-group]');
        const key = group?.getAttribute('data-group');

        if (!group) continue;

        if (row.classList.contains('is-same')) {
          group.classList.add('is-hidden');
          continue;
        }

        group.classList.toggle('is-hidden', lastVisibleGroup === key);
        lastVisibleGroup = key;
      }
    }

    #syncDots() {
      if (!this.#dotsHost || !this.#scroller) return;

      this.#layout = null;

      const desktop = this.#desktopQuery?.matches ?? window.matchMedia(DESKTOP_QUERY).matches;
      const layout = this.#measure();

      // Written after every read, so this cannot force a second layout.
      this.#scroller.style.scrollPaddingInlineStart = desktop ? '' : `${layout.gutter}px`;

      const pages = !desktop && layout.maxScroll > 1 ? pageIndexes(layout) : [];

      if (pages.length < 2) {
        for (const dot of this.#dotsHost.children) {
          dot.removeEventListener('click', this.#onDotClick);
        }

        this.#dotsHost.hidden = true;
        this.#dotsHost.replaceChildren();
        return;
      }

      this.#dotsHost.hidden = false;

      const pageKey = pages.join(',');
      const currentKey = Array.from(this.#dotsHost.children)
        .map((dot) => dot.dataset.comparisonDot)
        .join(',');

      if (pageKey !== currentKey) {
        for (const dot of this.#dotsHost.children) {
          dot.removeEventListener('click', this.#onDotClick);
        }

        const labelTemplate = this.dataset.dotLabel || '';
        const fragment = document.createDocumentFragment();

        for (const columnIndex of pages) {
          const dot = document.createElement('button');
          const productNumber = String(columnIndex + 1);

          dot.type = 'button';
          dot.className = 'product-comparison__dot';
          dot.dataset.comparisonDot = String(columnIndex);
          dot.setAttribute('aria-label', (labelTemplate || productNumber).replace('__n__', productNumber));
          dot.addEventListener('click', this.#onDotClick);
          fragment.append(dot);
        }

        this.#dotsHost.replaceChildren(fragment);
      }

      this.#updateActiveDot(layout.scrollLeft);
    }

    #updateActiveDot(scrollLeft = this.#scroller?.scrollLeft ?? 0) {
      if (!this.#dotsHost || this.#dotsHost.hidden || !this.#scroller) return;

      const dots = Array.from(this.#dotsHost.children);
      const leading = columnIndexAt(this.#measure(), scrollLeft);
      let active = 0;

      for (const [pageIndex, dot] of dots.entries()) {
        if (Number(dot.dataset.comparisonDot) <= leading) active = pageIndex;
      }

      for (const [index, dot] of dots.entries()) {
        dot.classList.toggle('is-active', index === active);
      }
    }
  }

  customElements.define(TAG, ProductComparison);
})();
